import * as THREE from 'three';

/*
 * The cleric's light: consecrated ground (sanctuary) and the ward shell around protected heroes.
 * Meshes are pooled; `sync()` places them once per sim tick, `frame()` turns the sigils and makes
 * the wards shimmer every animation frame. Everything is additive and pale (white, pale blue, a touch of
 * warm white for consecrated fire), drawn low and faint enough that the units on it stay readable.
 */

let sigilTex: THREE.Texture | null = null;
let runeTex: THREE.Texture | null = null;
let poolTex: THREE.Texture | null = null;
let columnTex: THREE.Texture | null = null;

const canvas = (w: number, h = w): [HTMLCanvasElement, CanvasRenderingContext2D] => {
  const c = document.createElement('canvas');
  c.width = w;
  c.height = h;
  return [c, c.getContext('2d') as CanvasRenderingContext2D];
};

const tex = (c: HTMLCanvasElement): THREE.Texture => {
  const t = new THREE.CanvasTexture(c);
  t.colorSpace = THREE.SRGBColorSpace;
  t.anisotropy = 4;
  return t;
};

/** Small angular glyphs, the same every time (seeded). */
export const glyph = (g: CanvasRenderingContext2D, x: number, y: number, s: number, seed: number): void => {
  let k = seed * 2654435761;
  const r = (): number => {
    k = (Math.imul(k ^ (k >>> 13), 1274126177) + 0x6d2b79f5) >>> 0;
    return k / 4294967296;
  };
  g.beginPath();
  g.moveTo(x, y - s);
  g.lineTo(x, y + s);
  const n = 1 + Math.floor(r() * 3);
  for (let i = 0; i < n; i++) {
    const yy = y - s + r() * 2 * s;
    const d = (r() < 0.5 ? -1 : 1) * s * (0.4 + r() * 0.5);
    g.moveTo(x, yy);
    g.lineTo(x + d, yy + (r() - 0.5) * s);
  }
  g.stroke();
};

/** The consecrated circle: a double ring of runes, an eight-pointed star and sun rays. White on clear. */
const sigil = (): THREE.Texture => {
  if (sigilTex) return sigilTex;
  const S = 512, C = S / 2;
  const [c, g] = canvas(S);
  g.strokeStyle = '#fff';
  g.lineCap = 'round';
  g.shadowColor = '#fff';
  g.shadowBlur = 10;
  const circle = (r: number, w: number): void => {
    g.lineWidth = w;
    g.beginPath();
    g.arc(C, C, r * C, 0, Math.PI * 2);
    g.stroke();
  };
  circle(0.95, 9);
  circle(0.86, 3);
  // Runes between the outer rings.
  g.lineWidth = 3;
  for (let i = 0; i < 28; i++) {
    const a = (i / 28) * Math.PI * 2;
    g.save();
    g.translate(C + Math.cos(a) * 0.905 * C, C + Math.sin(a) * 0.905 * C);
    g.rotate(a + Math.PI / 2);
    glyph(g, 0, 0, 0.026 * C, i + 3);
    g.restore();
  }
  circle(0.6, 2.5);
  // Eight-pointed star of the dawn: two squares, finer than the rings.
  g.lineWidth = 2;
  for (const off of [0, Math.PI / 4]) {
    g.beginPath();
    for (let i = 0; i <= 4; i++) {
      const a = off + (i / 4) * Math.PI * 2;
      const x = C + Math.cos(a) * 0.84 * C, y = C + Math.sin(a) * 0.84 * C;
      if (i === 0) g.moveTo(x, y);
      else g.lineTo(x, y);
    }
    g.stroke();
  }
  // Sun rays from the heart.
  for (let i = 0; i < 16; i++) {
    const a = (i / 16) * Math.PI * 2 + Math.PI / 16;
    const r1 = i % 2 ? 0.4 : 0.52;
    g.lineWidth = i % 2 ? 2 : 3.5;
    g.beginPath();
    g.moveTo(C + Math.cos(a) * 0.17 * C, C + Math.sin(a) * 0.17 * C);
    g.lineTo(C + Math.cos(a) * r1 * C, C + Math.sin(a) * r1 * C);
    g.stroke();
  }
  circle(0.15, 4);
  circle(0.07, 2);
  sigilTex = tex(c);
  return sigilTex;
};

/** A thinner ring of runes and dots that turns the other way. */
const runeRing = (): THREE.Texture => {
  if (runeTex) return runeTex;
  const S = 512, C = S / 2;
  const [c, g] = canvas(S);
  g.strokeStyle = '#fff';
  g.fillStyle = '#fff';
  g.lineCap = 'round';
  g.shadowColor = '#fff';
  g.shadowBlur = 8;
  g.lineWidth = 2;
  for (const r of [0.74, 0.66]) {
    g.beginPath();
    g.arc(C, C, r * C, 0, Math.PI * 2);
    g.stroke();
  }
  g.lineWidth = 2.5;
  for (let i = 0; i < 20; i++) {
    const a = (i / 20) * Math.PI * 2;
    g.save();
    g.translate(C + Math.cos(a) * 0.7 * C, C + Math.sin(a) * 0.7 * C);
    g.rotate(a + Math.PI / 2);
    glyph(g, 0, 0, 0.022 * C, i + 101);
    g.restore();
    const b = a + Math.PI / 20;
    g.beginPath();
    g.arc(C + Math.cos(b) * 0.7 * C, C + Math.sin(b) * 0.7 * C, 3, 0, Math.PI * 2);
    g.fill();
  }
  runeTex = tex(c);
  return runeTex;
};

/** Soft pool of light on the ground, brighter at the rim. */
const pool = (): THREE.Texture => {
  if (poolTex) return poolTex;
  const S = 256, C = S / 2;
  const [c, g] = canvas(S);
  const gr = g.createRadialGradient(C, C, 0, C, C, C);
  gr.addColorStop(0, 'rgba(255,255,255,0.55)');
  gr.addColorStop(0.7, 'rgba(255,255,255,0.32)');
  gr.addColorStop(0.93, 'rgba(255,255,255,0.75)');
  gr.addColorStop(1, 'rgba(255,255,255,0)');
  g.fillStyle = gr;
  g.fillRect(0, 0, S, S);
  poolTex = tex(c);
  return poolTex;
};

/** Column of light: fades upward, with faint vertical shafts. */
const column = (): THREE.Texture => {
  if (columnTex) return columnTex;
  const W = 256, H = 128;
  const [c, g] = canvas(W, H);
  const img = g.createImageData(W, H);
  let k = 7;
  const shaft: number[] = [];
  for (let x = 0; x < W; x++) {
    k = (Math.imul(k, 1103515245) + 12345) >>> 0;
    shaft.push(k / 4294967296);
  }
  for (let x = 0; x < W; x++) {
    // Smooth the shafts a little so they read as soft rays, not noise.
    const s = (shaft[x]! + shaft[(x + 1) % W]! + shaft[(x + W - 1) % W]!) / 3;
    for (let y = 0; y < H; y++) {
      const v = y / (H - 1); // 0 top, 1 bottom
      const a = Math.pow(v, 1.6) * (0.45 + 0.55 * s * s);
      const o = (y * W + x) * 4;
      img.data[o] = img.data[o + 1] = img.data[o + 2] = 255;
      img.data[o + 3] = Math.round(a * 255);
    }
  }
  g.putImageData(img, 0, 0);
  columnTex = tex(c);
  columnTex.wrapS = THREE.RepeatWrapping;
  return columnTex;
};

const additive = (map: THREE.Texture, color: string): THREE.MeshBasicMaterial =>
  new THREE.MeshBasicMaterial({ map, color, transparent: true, depthWrite: false, blending: THREE.AdditiveBlending, side: THREE.DoubleSide, forceSinglePass: true, opacity: 0 });

interface Sanctum {
  g: THREE.Group;
  pool: THREE.Mesh;
  sigil: THREE.Mesh;
  runes: THREE.Mesh;
  column: THREE.Mesh;
  /** Opacity the sim tick wants (fade in/out); `frame()` eases toward it. */
  alpha: number;
  shown: number;
  burn: boolean;
  spin: number;
}

const WARD_VERT = /* glsl */ `
varying vec3 vN;
varying vec3 vV;
varying float vY;
void main() {
  vec4 mv = modelViewMatrix * vec4(position, 1.0);
  vN = normalize(normalMatrix * normal);
  vV = normalize(-mv.xyz);
  vY = position.y;
  gl_Position = projectionMatrix * mv;
}`;

const WARD_FRAG = /* glsl */ `
uniform vec3 color;
uniform float opacity;
uniform float time;
varying vec3 vN;
varying vec3 vV;
varying float vY;
void main() {
  float rim = pow(1.0 - abs(dot(normalize(vN), normalize(vV))), 2.2);
  float band = 0.5 + 0.5 * sin(vY * 18.0 - time * 3.2);
  float a = (0.025 + rim * (0.5 + 0.3 * band)) * opacity;
  gl_FragColor = vec4(color * (0.8 + 0.4 * band), a);
  #include <colorspace_fragment>
}`;

interface Ward {
  mesh: THREE.Mesh;
  mat: THREE.ShaderMaterial;
}

export class HolyLayer {
  readonly group = new THREE.Group();
  private sanctums: Sanctum[] = [];
  private used = 0;
  private wards: Ward[] = [];
  private wardsUsed = 0;
  private born = new Map<number, number>();
  private wardGeo = new THREE.SphereGeometry(1, 28, 18);
  /** A ward far under the ground until the fight's compile ahead is done (it compiles only what is visible). */
  private readonly keep: THREE.Mesh;

  constructor() {
    // The first ward of a fight compiled its shader on the spot otherwise.
    this.keep = this.makeWard().mesh;
    this.wards.pop();
    this.keep.visible = true;
    this.keep.position.set(0, -60, 0);
    this.keep.scale.setScalar(0.0001);
    this.keep.frustumCulled = false;
  }

  /** The view's shaders are linked: the ward's keeper can go. */
  set primed(v: boolean) {
    this.keep.visible = !v;
  }

  /** Starts a sim tick: every pooled piece is hidden unless placed again. */
  begin(): void {
    this.used = 0;
    this.wardsUsed = 0;
  }

  /**
   * Places one sanctuary. `tick` is the sim tick, `left` its remaining ticks, `r` its world radius,
   * `burn` true when it is consecrated (it burns nightmares).
   */
  sanctuary(id: number, p: THREE.Vector3, r: number, tick: number, left: number, burn: boolean): void {
    const born = this.born.get(id) ?? tick;
    this.born.set(id, born);
    const s = this.sanctums[this.used++] ?? this.makeSanctum();
    s.g.visible = true;
    s.g.position.copy(p);
    s.g.scale.set(r, 1, r);
    const age = tick - born;
    // Opens over half a second (and grows from 70 %), dims over the last two thirds of a second.
    const open = Math.min(1, (age + 1) / 6);
    s.alpha = open * Math.min(1, left / 8);
    s.spin = age;
    const k = 0.7 + 0.3 * (1 - (1 - open) * (1 - open));
    s.sigil.scale.setScalar(k);
    s.runes.scale.setScalar(k * 1.02);
    if (s.burn !== burn) {
      s.burn = burn;
      (s.sigil.material as THREE.MeshBasicMaterial).color.set(burn ? '#fff1e4' : '#e6f2ff');
      (s.pool.material as THREE.MeshBasicMaterial).color.set(burn ? '#ffd2b0' : '#9fc6ff');
    }
  }

  ward(p: THREE.Vector3, size: number, strength: number): void {
    const w = this.wards[this.wardsUsed++] ?? this.makeWard();
    w.mesh.visible = true;
    w.mesh.position.set(p.x, p.y + 0.6 * size, p.z);
    w.mesh.scale.set(0.44 * size, 0.64 * size, 0.44 * size);
    (w.mat.uniforms.opacity as THREE.IUniform<number>).value = strength;
  }

  end(): void {
    for (let i = this.used; i < this.sanctums.length; i++) {
      const s = this.sanctums[i] as Sanctum;
      s.g.visible = false;
      s.alpha = s.shown = 0;
    }
    for (let i = this.wardsUsed; i < this.wards.length; i++) (this.wards[i] as Ward).mesh.visible = false;
    if (this.born.size > 64) this.born.clear();
  }

  /** Every animation frame: sigils turn, the column breathes, wards shimmer. */
  frame(now: number): void {
    for (let i = 0; i < this.used; i++) {
      const s = this.sanctums[i] as Sanctum;
      s.shown += (s.alpha - s.shown) * 0.25;
      const a = s.shown;
      const breath = 0.85 + 0.15 * Math.sin(now * 2.1 + i);
      (s.sigil.material as THREE.MeshBasicMaterial).opacity = 0.62 * a * breath;
      (s.runes.material as THREE.MeshBasicMaterial).opacity = 0.5 * a;
      (s.pool.material as THREE.MeshBasicMaterial).opacity = 0.22 * a;
      (s.column.material as THREE.MeshBasicMaterial).opacity = 0.16 * a * breath;
      s.sigil.rotation.z = now * 0.12;
      s.runes.rotation.z = -now * 0.3;
      const cm = (s.column.material as THREE.MeshBasicMaterial).map as THREE.Texture;
      cm.offset.x = (now * 0.02) % 1;
    }
    for (let i = 0; i < this.wardsUsed; i++) {
      const w = this.wards[i] as Ward;
      (w.mat.uniforms.time as THREE.IUniform<number>).value = now + i * 0.7;
      w.mesh.rotation.y = now * 0.4;
    }
  }

  private makeSanctum(): Sanctum {
    const g = new THREE.Group();
    const flat = (map: THREE.Texture, color: string, y: number, order: number): THREE.Mesh => {
      const m = new THREE.Mesh(new THREE.CircleGeometry(1, 64), additive(map, color));
      m.rotation.x = -Math.PI / 2;
      m.position.y = y;
      m.renderOrder = order;
      g.add(m);
      return m;
    };
    const poolM = flat(pool(), '#9fc6ff', 0.01, 2);
    // Sigil meshes are rotated about their own z (= world up after the flat rotation).
    const sigilM = flat(sigil(), '#e6f2ff', 0.02, 3);
    const runesM = flat(runeRing(), '#cfe4ff', 0.025, 3);
    const colT = column().clone();
    colT.wrapS = THREE.RepeatWrapping;
    colT.repeat.x = 2;
    colT.needsUpdate = true;
    const col = new THREE.Mesh(new THREE.CylinderGeometry(0.9, 0.96, 2.4, 40, 1, true), additive(colT, '#d6e8ff'));
    col.position.y = 1.2;
    col.renderOrder = 4;
    g.add(col);
    g.visible = false;
    this.group.add(g);
    const s: Sanctum = { g, pool: poolM, sigil: sigilM, runes: runesM, column: col, alpha: 0, shown: 0, burn: false, spin: 0 };
    this.sanctums.push(s);
    return s;
  }

  private makeWard(): Ward {
    const mat = new THREE.ShaderMaterial({
      uniforms: { color: { value: new THREE.Color('#bfe0ff') }, opacity: { value: 1 }, time: { value: 0 } },
      vertexShader: WARD_VERT,
      fragmentShader: WARD_FRAG,
      transparent: true,
      depthWrite: false,
      blending: THREE.AdditiveBlending,
      side: THREE.DoubleSide, forceSinglePass: true,
    });
    const mesh = new THREE.Mesh(this.wardGeo, mat);
    mesh.renderOrder = 11;
    mesh.visible = false;
    this.group.add(mesh);
    const w = { mesh, mat };
    this.wards.push(w);
    return w;
  }
}
